/*
 * Kivora Home — global deal helpers (FOUNDATION). Loaded with `defer` on every page.
 *
 *  window.KvMoney   format cents like Liquid's `money` filter (presentment currency aware)
 *  <kv-shop-money data-cents>  shop-currency threshold → shopper's currency (rate, rounded up)
 *  <kv-countdown data-end="ISO">   real countdown, DD:HH:MM:SS, one shared 1 s ticker,
 *                   hides itself + closest [data-kv-deal-scope] at zero. Never resets.
 *  window.KvDeals   expiry sweep for cached HTML:
 *                     [data-kv-expires="ISO"]      → hidden once ISO has passed
 *                     [data-kv-reveal-after="ISO"] → un-hidden once ISO has passed
 *                     [data-kv-deal-end="ISO"][data-kv-deal-class="a b"] → classes removed
 */
(function () {
  'use strict';

  var theme = window.KvTheme || {};

  /* ------------------------------------------------------------------ */
  /* KvMoney                                                              */
  /* ------------------------------------------------------------------ */
  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function addDelimiters(cents, precision, thousands, decimal) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var fixed = (n / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function applyFormat(cents, format) {
    var match = (format || '${{amount}}').match(PLACEHOLDER);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals':
        value = addDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = addDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = addDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = addDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = addDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = addDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = addDelimiters(cents, 2, ',', '.');
    }
    return match ? format.replace(PLACEHOLDER, value) : value;
  }

  /* Liquid renders 123456 | money for the visitor's presentment currency; we turn
     that sample back into a format string so JS output matches Liquid output. */
  var SAMPLE_PATTERNS = [
    ['1,234.56', '{{amount}}'],
    ['1.234,56', '{{amount_with_comma_separator}}'],
    ["1'234.56", '{{amount_with_apostrophe_separator}}'],
    ['1 234,56', '{{amount_with_space_separator}}'],
    ['1 234.56', '{{amount_with_period_and_space_separator}}'],
    ['1,235', '{{amount_no_decimals}}'],
    ['1.235', '{{amount_no_decimals_with_comma_separator}}'],
    ['1 235', '{{amount_no_decimals_with_space_separator}}'],
  ];

  function formatFromSample(sample) {
    if (!sample) return null;
    var s = String(sample).replace(/ /g, ' ');
    for (var i = 0; i < SAMPLE_PATTERNS.length; i++) {
      if (s.indexOf(SAMPLE_PATTERNS[i][0]) !== -1) return s.replace(SAMPLE_PATTERNS[i][0], SAMPLE_PATTERNS[i][1]);
    }
    return null;
  }

  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || theme.currency || null;
  }

  function resolveFormat(withCurrency) {
    var fromSample = formatFromSample(withCurrency ? theme.moneyWithCurrencySample : theme.moneySample);
    if (fromSample && (!theme.currency || activeCurrency() === theme.currency)) return fromSample;
    if (!activeCurrency() || activeCurrency() === theme.shopCurrency) {
      return withCurrency ? theme.moneyWithCurrencyFormat || theme.moneyFormat : theme.moneyFormat;
    }
    return null;
  }

  var KvMoney = {
    /** Format integer cents, e.g. KvMoney.format(999) → "$9.99". */
    format: function (cents, options) {
      var withCurrency = !!(options && options.withCurrency);
      var fmt = resolveFormat(withCurrency);
      if (fmt) return applyFormat(cents, fmt);
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: activeCurrency() || 'USD',
        }).format(Number(cents) / 100);
      } catch (e) {
        return applyFormat(cents, '${{amount}}');
      }
    },

    /** Whole / fraction split for the shelf tag: {formatted, main, cents}. */
    parts: function (cents) {
      var formatted = KvMoney.format(cents);
      var idx = formatted.lastIndexOf('.');
      if (idx === -1 || !/^\.\d{2}/.test(formatted.slice(idx))) return { formatted: formatted, main: formatted, cents: '' };
      return { formatted: formatted, main: formatted.slice(0, idx), cents: formatted.slice(idx) };
    },

    /** Same markup as snippets/kv-price-tag.liquid (tag only). */
    tagHTML: function (cents, options) {
      var o = options || {};
      var p = KvMoney.parts(cents);
      var size = o.size || 'sm';
      var cls = 'kv-price-tag kv-price-tag--' + size + (o.deal ? ' kv-price-tag--deal' : '') + (o.soldOut ? ' kv-price-tag--soldout' : '');
      var esc = function (str) {
        return String(str).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      };
      return (
        '<span class="' + cls + '">' +
        '<span class="visually-hidden">' + esc((o.label ? o.label + ' ' : '') + p.formatted) + '</span>' +
        '<span class="kv-price-tag__inner" aria-hidden="true"><span class="kv-price-tag__main">' + esc(p.main) + '</span>' +
        (p.cents ? '<sup class="kv-price-tag__cents">' + esc(p.cents) + '</sup>' : '') +
        '</span></span>'
      );
    },
  };

  window.KvMoney = window.KvMoney || KvMoney;

  /* ------------------------------------------------------------------ */
  /* <kv-shop-money data-cents="6000" [data-trim]>                        */
  /* A threshold stored in the SHOP currency (a discount minimum). Liquid  */
  /* can't convert it, so the server prints the shop amount ("$60 USD")    */
  /* and this element re-expresses it in the shopper's currency with       */
  /* Shopify.currency.rate, rounded UP to the cent so a "spend over X"     */
  /* claim is never lower than the real converted minimum.                 */
  /* ------------------------------------------------------------------ */
  function currencyRate() {
    var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return isFinite(r) && r > 0 ? r : 1;
  }

  class KvShopMoney extends HTMLElement {
    connectedCallback() {
      var cents = parseInt(this.dataset.cents, 10);
      var active = activeCurrency();
      if (isNaN(cents) || !active || active === theme.shopCurrency) return;
      var converted = Math.ceil(cents * currencyRate() - 1e-6);
      var text = KvMoney.format(converted);
      if (this.hasAttribute('data-trim') && converted % 100 === 0) text = text.replace(/[.,]00(?!\d)/, '');
      this.textContent = text;
      this.classList.add('is-ready');
    }
  }

  if (!customElements.get('kv-shop-money')) customElements.define('kv-shop-money', KvShopMoney);

  /* ------------------------------------------------------------------ */
  /* Expiry sweep for cached deal markup                                  */
  /* ------------------------------------------------------------------ */
  var SWEEP_SELECTOR = '[data-kv-expires],[data-kv-reveal-after],[data-kv-deal-end]';
  var sweepTimer = null;

  function parseTime(iso) {
    var t = Date.parse(iso);
    return isNaN(t) ? null : t;
  }

  function sweep() {
    var now = Date.now();
    var next = Infinity;
    var expiredSomething = false;
    document.querySelectorAll(SWEEP_SELECTOR).forEach(function (el) {
      var iso = el.getAttribute('data-kv-expires') || el.getAttribute('data-kv-reveal-after') || el.getAttribute('data-kv-deal-end');
      var t = parseTime(iso);
      if (t === null) return;
      if (now < t) {
        if (t < next) next = t;
        return;
      }
      expiredSomething = true;
      if (el.hasAttribute('data-kv-expires')) {
        el.hidden = true;
        el.removeAttribute('data-kv-expires');
      }
      if (el.hasAttribute('data-kv-reveal-after')) {
        el.hidden = false;
        el.removeAttribute('data-kv-reveal-after');
      }
      if (el.hasAttribute('data-kv-deal-end')) {
        (el.getAttribute('data-kv-deal-class') || '').split(/\s+/).forEach(function (c) {
          if (c) el.classList.remove(c);
        });
        el.removeAttribute('data-kv-deal-end');
      }
    });
    if (expiredSomething) document.dispatchEvent(new CustomEvent('kv:deal-expired'));
    clearTimeout(sweepTimer);
    if (next !== Infinity) {
      sweepTimer = setTimeout(sweep, Math.min(Math.max(next - Date.now() + 50, 50), 2147483000));
    }
  }

  window.KvDeals = window.KvDeals || {
    sweep: sweep,
    /** true while an ISO end time is still in the future */
    isLive: function (iso) {
      var t = parseTime(iso || theme.dealEnds);
      return t !== null && Date.now() < t;
    },
  };

  /* ------------------------------------------------------------------ */
  /* <kv-countdown data-end="ISO">                                        */
  /* ------------------------------------------------------------------ */
  var active = new Set();
  var ticker = null;

  function tickAll() {
    var now = Date.now();
    active.forEach(function (el) {
      el.update(now);
    });
    if (!active.size && ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  function startTicker() {
    if (!ticker) ticker = setInterval(tickAll, 1000);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  var UNITS = ['d', 'h', 'm', 's'];
  var DEFAULT_LABELS = { d: 'Days', h: 'Hrs', m: 'Min', s: 'Sec' };

  class KvCountdown extends HTMLElement {
    connectedCallback() {
      this.end = parseTime(this.dataset.end);
      if (this.end === null) {
        // No valid end date → never show a timer we can't stand behind.
        this.hidden = true;
        return;
      }
      if (!this.querySelector('[data-unit]')) this.build();
      this.nums = {};
      var self = this;
      UNITS.forEach(function (u) {
        self.nums[u] = self.querySelector('[data-unit="' + u + '"]');
      });
      this.sr = this.querySelector('[data-kv-cd-sr]');
      this.lastMinuteKey = null;
      this.update(Date.now());
      if (!this.expired) {
        active.add(this);
        startTicker();
      }
    }

    disconnectedCallback() {
      active.delete(this);
    }

    build() {
      var labels = this.dataset;
      var html = '<span class="visually-hidden" aria-live="polite" data-kv-cd-sr></span><span class="kv-countdown__units" aria-hidden="true">';
      UNITS.forEach(function (u, i) {
        if (i) html += '<span class="kv-countdown__sep">:</span>';
        html +=
          '<span class="kv-countdown__unit"><span class="kv-countdown__num" data-unit="' + u + '">00</span>' +
          '<span class="kv-countdown__label">' + (labels['label' + u.toUpperCase()] || DEFAULT_LABELS[u]) + '</span></span>';
      });
      html += '</span>';
      this.innerHTML = html;
      this.classList.add('kv-countdown');
    }

    update(now) {
      var left = Math.floor((this.end - now) / 1000);
      if (left <= 0) {
        this.expire();
        return;
      }
      var parts = {
        d: Math.floor(left / 86400),
        h: Math.floor((left % 86400) / 3600),
        m: Math.floor((left % 3600) / 60),
        s: left % 60,
      };
      var nums = this.nums;
      // Last 24 hours: drop the "00 days" unit (kv-deals.css 7b).
      this.toggleAttribute('data-no-days', parts.d === 0);
      UNITS.forEach(function (u) {
        var el = nums[u];
        if (!el) return;
        var txt = pad(parts[u]);
        if (el.textContent !== txt) el.textContent = txt;
      });
      // Screen readers hear the time once a minute, never every second.
      var minuteKey = parts.d + ':' + parts.h + ':' + parts.m;
      if (this.sr && minuteKey !== this.lastMinuteKey) {
        this.lastMinuteKey = minuteKey;
        var ds = this.dataset;
        this.sr.textContent =
          (ds.srPrefix ? ds.srPrefix + ' ' : '') +
          parts.d + ' ' + (ds.srDays || 'days') + ', ' +
          parts.h + ' ' + (ds.srHours || 'hours') + ', ' +
          parts.m + ' ' + (ds.srMinutes || 'minutes');
      }
    }

    expire() {
      if (this.expired) return;
      this.expired = true;
      active.delete(this);
      this.hidden = true;
      this.setAttribute('data-expired', '');
      var scope = this.closest('[data-kv-deal-scope]');
      if (scope) scope.hidden = true;
      this.dispatchEvent(new CustomEvent('kv:countdown-end', { bubbles: true }));
      sweep();
    }
  }

  if (!customElements.get('kv-countdown')) customElements.define('kv-countdown', KvCountdown);

  /* ------------------------------------------------------------------ */
  /* Wiring                                                               */
  /* ------------------------------------------------------------------ */
  function runSoon() {
    setTimeout(sweep, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep, { once: true });
  } else {
    sweep();
  }

  document.addEventListener('shopify:section:load', runSoon);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      tickAll();
      sweep();
    }
  });

  // Dawn's constants.js declares PUB_SUB_EVENTS with `const` (global lexical scope,
  // not a window property), so look it up by name.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, runSoon);
    if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, runSoon);
  }
})();
